import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import { Grid, DialogContent, Box } from '@mui/material';
import { useEffect, useState, useCallback } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { FormProvider, useForm } from 'react-hook-form';
import { Cell } from 'react-table';
import {
  IFile,
  createClosureFile,
  getClosureFileList,
  ClosureFileDTO,
  IClientSettings,
  getCurrentWorkspaceFileCategories,
  saveActivityLog,
  LogEntityType,
  LogContextType,
  LogAction,
} from 'wisestep-api';
import {
  EmptyTeamsAndUsersIcon,
  WsDataTable,
  getFormatDate,
  WsButton,
  WsDrawer,
  ControlledTextInput,
  WsDatePicker,
  WsFilePreview,
} from 'wisestep-base';

import NoDataFoundCard from '../../../components/noDataFoundCard';
import { activityLogDTOHelper } from '../../../features/activityLog/ActivityHelper';
import { AutocompleteWithPersistentAdd } from '../../../features/clientDetails/components/contractArrangementsTab/AddEditFeeArrangement';
import UploadFile from '../../../features/clientDetails/components/uploadFile/UploadFile';
import FileCategoryForm from '../../../features/clientsSettings/components/FileCategoryForm';
import { downloadCV } from '../../../features/conversationWidget/utils/cvHelper';
import { ClosureAction, useClosureAuthz } from '../../../hooks/useClosureAuthz';
import { useDialog } from '../../../hooks/useDialog';
import ErrorLogger from '../../../providers/ErrorLogger';
import { failed, success } from '../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../utils/NotificationStore';

import { closureFilePayload, IClosureDocumentsProps } from './../helper/payload';

const ClosureDocuments = ({ closureId, showEditClosure }: IClosureDocumentsProps) => {
  const methods = useForm();
  const {
    formState: { dirtyFields },
  } = methods;
  const [documentListData, setDocumentListData] = useState<ClosureFileDTO[]>();
  const [openUploadFileDialog, setOpenUploadFileDialog] = useState<boolean>(false);
  const [refresh, setRefresh] = useState<boolean>(false);
  const setNotification = useNotificationStore((state) => state.setNotification);
  const [selectedFile, setSelectedFile] = useState<ClosureFileDTO>();
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const watchFileCategoryType = methods.watch('fileCategoryType');
  const [fileCategories, SetFileCategories] = useState<IClientSettings[]>([]);
  const [openDialog, closeDialog] = useDialog();
  const showAddDocuments = useClosureAuthz(ClosureAction.ADD_CLOSURE_DETAILS_ADD_DOCUMENTS);
  const showEditDocument = useClosureAuthz(ClosureAction.CLOSURE_DETAILS_EDIT_DOCUMENTS);

  const getClosureFileListData = useCallback(async () => {
    const response = await getClosureFileList(closureId);
    setDocumentListData(response.data);
  }, [closureId]);

  const fetchFileCategories = useCallback(async () => {
    const response = await getCurrentWorkspaceFileCategories();
    SetFileCategories(response.data);
  }, []);

  useEffect(() => {
    getClosureFileListData();
    fetchFileCategories();
  }, [fetchFileCategories, getClosureFileListData]);

  const getColumns = () => {
    return documentListData ? columns.filter((item) => item.accessor != 'childCount') : columns;
  };

  const handleDialog = (value: boolean) => setOpenUploadFileDialog(value);

  const addDocument = () => {
    methods.setValue('fileCategoryType', '');
    methods.setValue('documentName', '');
    methods.setValue('validFrom', '');
    methods.setValue('validTill', '');
    setIsEdit(false);
    setSelectedFile(undefined);
    setOpenUploadFileDialog(true);
  };

  const saveFile = async (file: IFile) => {
    const payload = { ...closureFilePayload };
    let response;
    try {
      const avPayload = { ...activityLogDTOHelper };
      const avChangedValueViaDirtyFileds = Object.keys(dirtyFields).toString().split(',');

      if (file.location) {
        payload.closureId = closureId;
        file.location = file.location.split('?')[0];
        payload.file = file;
        payload.documentName = methods.getValues('documentName');
        payload.documentType = methods.getValues('fileCategoryType').name;
        payload.validFrom = methods.getValues('validFrom');
        payload.validTo = methods.getValues('validTill');
        payload.id = selectedFile?.id ?? '';

        if (isEdit) {
          payload.createdOn = selectedFile?.createdOn ?? null;
          payload.createdBy = selectedFile?.createdBy ?? '';
          payload.createdByName = selectedFile?.createdByName ?? '';
          avPayload.action = LogAction.Edit;
          avPayload.entityId = selectedFile?.id as string;

          avPayload.description = `EDITED ${avChangedValueViaDirtyFileds} in document ${payload.documentType} under Placement`;
        }

        response = await createClosureFile(payload);

        if (!isEdit) {
          avPayload.entityId = response.data.id;
          avPayload.description = `ADDED ${payload.documentType} under Placement`;
        }

        avPayload.parentEntitiyType = LogContextType.Placement;
        avPayload.parentEntitiyId = payload.closureId;
        avPayload.entityType = LogEntityType.Documents;
        avPayload.field = `Documents`;
        avPayload.entityName = payload.documentName;
        avPayload.contextData = {
          closureId: payload.closureId,
        };
        await saveActivityLog(avPayload);
        setRefresh(!refresh);
        handleDialog(false);
        setNotification(success('File has been saved successfully !!'));
        getClosureFileListData();
        setSelectedFile(undefined);
        setIsEdit(false);
      }
    } catch {
      setNotification(failed('Failed to save file, Please try again !!'));
    }
  };

  const onCancel = () => {
    setSelectedFile(undefined);
    setIsEdit(false);
  };

  const handleEdit = (index: string) => {
    if (index && documentListData) {
      const file = documentListData[index];
      methods.setValue('fileCategoryType', file.documentType);
      methods.setValue('documentName', file.documentName);
      methods.setValue('validFrom', file.validFrom);
      methods.setValue('validTill', file.validTo);
      setSelectedFile(file);
      setOpenUploadFileDialog(true);
      setIsEdit(true);
    } else setNotification(failed('Failed fetch selected file details, Please try again !!'));
  };

  const columns = [
    {
      Header: 'Document Type',
      accessor: 'documentType',
    },
    {
      Header: 'Document Name',
      accessor: 'documentName',
    },
    {
      Header: 'Validity From',
      accessor: 'validFrom',
      Cell: (cell: Cell) => getFormatDate(cell.value),
    },
    {
      Header: 'Validity To',
      accessor: 'validTo',
      Cell: (cell: Cell) => getFormatDate(cell.value),
    },
    {
      Header: 'Uploaded Date',
      accessor: 'createdOn',
      Cell: (cell: Cell) => getFormatDate(cell.value),
    },
    {
      Header: 'Uploaded By',
      accessor: 'createdByName',
    },
    {
      Header: '',
      accessor: 'details',
      Cell: (cell: Cell) => (
        <WsFilePreview
          url={cell.row?.original['file']['location']}
          requireDownloadIcon={true}
          hideViewIcon={true}
          onDownload={() =>
            downloadCV(cell.row?.original['file']['location'], cell.row?.original['file']['name'])
          }
        />
      ),
    },
  ];

  return (
    <Grid container sx={{ display: 'flex' }}>
      <Grid item xs={12} pt={2} pb={2} container justifyContent={'flex-end'}>
        {showAddDocuments && (
          <WsButton
            onClick={() => addDocument()}
            variant="contained"
            size="large"
            startIcon={<AddOutlinedIcon />}
          >
            Add Documents
          </WsButton>
        )}
      </Grid>

      {documentListData && documentListData?.length > 0 ? (
        <WsDataTable
          columns={getColumns()}
          data={documentListData as []}
          selectable={false}
          boldItems={['name']}
          dense={false}
          rowActions={
            showEditDocument && showEditClosure
              ? [
                  {
                    label: 'Edit',
                    callback: (cell: Cell[]) => handleEdit(cell[0]?.row?.id ?? ''),
                  },
                ]
              : []
          }
        />
      ) : (
        <NoDataFoundCard text="No results found." icon={<EmptyTeamsAndUsersIcon />} />
      )}
      {openUploadFileDialog && (
        <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
          <WsDrawer
            open={openUploadFileDialog}
            size={'medium'}
            onClose={() => handleDialog(false)}
            title={isEdit ? 'Edit Document' : 'upload Document'}
          >
            <FormProvider {...methods}>
              <DialogContent>
                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <Box pb={watchFileCategoryType ? 0 : 2}>
                      <AutocompleteWithPersistentAdd
                        label="Document Type"
                        name="fileCategoryType"
                        options={fileCategories}
                        placeholder="Select Document type"
                        required={true}
                        optionLabelKey="name"
                        creatable
                        persistentAddOption
                        onCreate={({ inputValue: fileCategoryName }) => {
                          openDialog({
                            children: (
                              <FileCategoryForm
                                onCancel={() => {
                                  closeDialog();
                                }}
                                onSave={async (newFileCategory) => {
                                  await fetchFileCategories();
                                  closeDialog();
                                  methods.setValue('fileCategoryType', newFileCategory);
                                }}
                                defaultValues={{ name: fileCategoryName.trim() }}
                              />
                            ),
                          });
                        }}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={8}>
                    <ControlledTextInput
                      name="documentName"
                      type="text"
                      fullWidth
                      id="outlined-basic"
                      placeholder="Document Name"
                      variant="outlined"
                      labelText="Document Name"
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={6} sm={4}>
                    <WsDatePicker name="validFrom" label="Validity From" />
                  </Grid>
                  <Grid item xs={6} sm={4}>
                    <WsDatePicker name="validTill" label="Validity Till" />
                  </Grid>
                </Grid>
                <UploadFile onCancel={onCancel} onContinue={saveFile} />
              </DialogContent>
            </FormProvider>
          </WsDrawer>
        </ErrorBoundary>
      )}
    </Grid>
  );
};

export default ClosureDocuments;
